import { FC, useEffect, useMemo, useState, type JSX } from 'react';
import { offerApis } from '../apis/offerApis';
import { FormContainer } from '../components/SelTradeForm.styled';
import { initialOfferedProduct, initialSellTradeState, initialWantedProduct } from '../mock';
import { OffererForm } from './OffererForm';
import { WatchesForm } from './WatchesForm';
import {
  SellTradeFormType,
  OfferType,
  WantedProductType,
  OfferedProductType,
  OfferTypeEnum,
  ImageType,
} from '../types/SellTradeForm.types';
import validator from 'validator';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { reducerTypes } from '../../../store/Alert/types';
import { toggleLoadingAction } from '../../../store/Loading/action';
import stepSuccesfullIcon from '../../../assets/images/svg/stepSuccesfullIcon.svg';
import Image from 'next/image';
import LocalizedLink from '../../../components/Link/localized-link';
import { usePathname } from 'next/navigation';
import { sendGTMEvent } from '@next/third-parties/google';
import { Typography } from '@mui/material';

type Props = {
  offerType: OfferTypeEnum;
  forModal?: boolean;
  manageModal?: () => void;
};

const SellTradeForm: FC<Props> = ({ offerType, forModal, manageModal }) => {
  const pathName = usePathname();
  const [loading] = useState<boolean>(false);
  const { product } = useAppSelector((st) => st.products);
  const [sellTradeState, setSellTradeState] = useState<SellTradeFormType>(
    forModal
      ? {
          ...initialSellTradeState,
          wantedProducts: [
            {
              productId: product?.id || '',
              brandName: product?.model?.brand?.name || '',
              productName: product?.model?.name || '',
              refNumber: product?.refNumber || '',
              comment: '',
            },
          ],
        }
      : initialSellTradeState
  );
  const [offererInfoAdded, setOffererInfoAdded] = useState<boolean>(false);
  const [tradeMode, setTradeMode] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [stepArray, setStepArray] = useState<number[]>([1, 2]);
  const dispatch = useAppDispatch();
  const locale = useAppSelector((st) => st.locale);

  const messages = useMemo(() => {
    const key = forModal ? 'make_an_offer' : `${OfferTypeEnum[offerType]?.toLowerCase()}_form_title`;

    return {
      title: locale[key],
      alert_incorrect_phone: locale['sell_trade_form_alert_incorrect_phone'],
      alert_brand_has_to_be_selected: locale['sell_trade_form_alert_brand_has_to_be_selected'],
      alert_failed: locale['sell_trade_form_alert_failed'],
      link_0: locale['sell_trade_link_main_page'],
      link_1: locale['sell_trade_link_sell_trade_page'],
    };
  }, [forModal, locale, offerType]);

  if (step === 3 && offerType !== 1) {
    setStep(2);
  }

  useEffect(() => {
    if (offerType === 1 && stepArray.length !== 3) {
      setStepArray([1, 2, 3]);
    }
    if (offerType !== 1 && stepArray.length === 3) {
      setStepArray([1, 2]);
    }
    setStep(1);
  }, [offerType, pathName, stepArray.length]);

  useEffect(() => {
    setSellTradeState((prev) => ({
      ...initialSellTradeState,
      offer: {
        ...initialSellTradeState.offer,
        offerType,
        phoneCode: prev.offer.phoneCode,
      },
      wantedProducts:
        offerType === OfferTypeEnum.Sell || offerType === OfferTypeEnum.Commission
          ? []
          : forModal
          ? [
              {
                productId: product?.id || '',
                brandName: product?.model?.brand?.name || '',
                productName: product?.model?.name || '',
                refNumber: product?.refNumber || '',
                comment: '',
              },
            ]
          : initialSellTradeState.wantedProducts,
    }));
    setOffererInfoAdded(false);
  }, [forModal, offerType, product?.id, product?.model?.brand?.name, product?.model?.name, product?.refNumber]);

  function setStepForTrade(payload: number) {
    setStep(payload);
  }

  function manageOffer(propName: keyof OfferType, payload: string | number | boolean | JSX.Element) {
    setSellTradeState((prev) => ({
      ...prev,
      offer: { ...prev.offer, [propName]: payload },
    }));
  }

  function removeImage(image: ImageType, index: number) {
    setSellTradeState((prev) => ({
      ...prev,
      offeredProducts: prev.offeredProducts?.map((product, i) =>
        index === i
          ? {
              ...product,
              images: product.images?.filter((item) => item?.id !== image?.id),
            }
          : product
      ),
    }));
  }

  function manageOfferedProducts(propName: keyof OfferedProductType, payload: any, index: number = 0) {
    let arr: ImageType[] = [];
    if (Array.isArray(payload)) {
      arr = payload;
    }
    if (propName === 'brandName') {
      setSellTradeState((prev) => ({
        ...prev,
        offeredProducts: prev.offeredProducts.map((prod, i) =>
          index === i
            ? {
                ...prod,
                brandName: payload,
                productName: '',
              }
            : prod
        ),
      }));
      return;
    }
    setSellTradeState((prev) => ({
      ...prev,
      offeredProducts: prev.offeredProducts.map((prod, i) =>
        index === i
          ? {
              ...prod,
              [propName]: propName === 'images' ? [...(prod.images || []), ...arr] : payload,
            }
          : prod
      ),
    }));
  }

  function manageWantedProducts(propName: keyof WantedProductType, payload: any, index: number = 0) {
    if (propName === 'brandName') {
      setSellTradeState((prev) => ({
        ...prev,
        wantedProducts: prev.wantedProducts.map((prod, i) =>
          index === i
            ? {
                ...prod,
                brandName: payload,
                productName: '',
              }
            : prod
        ),
      }));
      return;
    }
    setSellTradeState((prev) => ({
      ...prev,
      wantedProducts: prev.wantedProducts.map((prod, i) =>
        index === i
          ? {
              ...prod,
              [propName]: payload,
            }
          : prod
      ),
    }));
  }

  function addOffer() {
    if (!validateOffererInfo()) {
      alert(`${messages.alert_incorrect_phone}`);
      return;
    }
    if (!forModal) {
      setOffererInfoAdded(true);
    }
    if (!tradeMode && forModal) {
      applyOffer(OfferTypeEnum.Buy);
    }
    setStep(2);
  }

  function addProduct(productType: string) {
    switch (productType) {
      case 'wanted':
        setSellTradeState((prev) => ({
          ...prev,
          wantedProducts: [...prev.wantedProducts, initialWantedProduct],
        }));
        break;
      case 'offered':
        setSellTradeState((prev) => ({
          ...prev,
          offeredProducts: [...prev.offeredProducts, initialOfferedProduct],
        }));
        break;
    }
  }

  async function applyOffer(oType?: OfferTypeEnum) {
    if (!validateOffererInfo()) {
      alert(`${messages.alert_incorrect_phone}`);
      return;
    }
    if (oType !== OfferTypeEnum.Buy && validateWatchInfo()) {
      alert(`${messages.alert_brand_has_to_be_selected}`);
      return;
    }
    try {
      dispatch(toggleLoadingAction());
      const reqData = JSON.parse(JSON.stringify(sellTradeState));
      reqData.offer.phone = reqData.offer.phone ? `${reqData.offer.phoneCode}${reqData.offer.phone}` : null;
      reqData.offer.email = reqData.offer.email ? reqData.offer.email : null;
      reqData.offeredProducts = sellTradeState.offeredProducts.map((item) => ({
        ...item,
        images: item.images?.map((image) => image?.data),
        productName: item.productName || '',
      }));
      reqData.wantedProducts = sellTradeState?.wantedProducts?.map((item) => ({
        ...item,
        productId: item.productId || null,
        productName: item.productName || '',
      }));

      if (oType === OfferTypeEnum.Buy) {
        reqData.offer.offerType = oType;
        reqData.offeredProducts = [];
      }

      switch (oType) {
        case OfferTypeEnum.Sell:
          sendGTMEvent({ event: 'form_sell' });
          break;
        case OfferTypeEnum.Commission:
          sendGTMEvent({ event: 'form_appraisal' });
          break;
        case OfferTypeEnum.Trade:
          sendGTMEvent({ event: 'form_exchange' });
          break;
        case OfferTypeEnum.Buy:
          sendGTMEvent({ event: 'form_purchase' });
          break;
      }

      await offerApis.apply(reqData);
      dispatch({ type: reducerTypes.TOGGLE_ALERT });
      manageModal && manageModal();
    } catch (e) {
      alert(`${messages.alert_failed}`);
    } finally {
      dispatch(toggleLoadingAction());
    }
  }

  function validateOffererInfo() {
    let errors = [];
    if (!validator.isEmail(sellTradeState.offer.email || '')) {
      errors.push('email');
    }
    if (!validator.isMobilePhone(sellTradeState.offer.phone || '')) {
      errors.push('phone');
    }
    return errors.length < 2;
  }

  function validateWatchInfo() {
    const invalidWantedProducts = sellTradeState.wantedProducts?.some((item) => !item.brandName);
    const invalidOfferedProducts = sellTradeState.offeredProducts?.some((item) => !item.brandName);

    return invalidWantedProducts || invalidOfferedProducts;
  }

  return (
    <FormContainer forModal={forModal}>
      {!forModal ? (
        <div className='page-link'>
          <Typography variant='h4' style={{ lineHeight: '20px', letterSpacing: '2px', color: '#2D2E30' }}>
            <LocalizedLink href={'/'}>
              <span className='link'>{messages.link_0}</span>
            </LocalizedLink>{' '}
            /{' '}
            <LocalizedLink href={'/sell-trade'}>
              <span className='link'>{messages.link_1}</span>
            </LocalizedLink>
          </Typography>
        </div>
      ) : null}

      <Typography variant='h1' className={`${forModal ? 'titleForModal' : 'title'}`}>
        {' '}
        {messages.title}
      </Typography>
      {!forModal ? (
        <div className='step-container'>
          {stepArray?.map((item, index) => (
            <div
              key={index}
              className={`step-item ${step === item ? 'step-item-active' : ''} ${
                step > item ? 'step-item-succesfull' : ''
              }`}
            >
              {step > item ? <Image src={stepSuccesfullIcon} alt='icon' /> : null}
              <Typography variant='h4' style={{ fontSize: '24px', lineHeight: '29px', letterSpacing: '1px' }}>
                {locale.sell_trade_generic_step} {item}
              </Typography>
            </div>
          ))}
        </div>
      ) : null}

      {forModal || step === 1 ? (
        <OffererForm
          data={sellTradeState.offer}
          manageData={manageOffer}
          addData={addOffer}
          offererInfoAdded={offererInfoAdded}
          forModal={forModal}
          tradeMode={tradeMode}
          manageTradeMode={(mode) => setTradeMode(mode)}
          loading={loading}
          manageModal={manageModal}
        />
      ) : null}
      {!forModal || (forModal && tradeMode) ? (
        <>
          {forModal || step === 2 || step === 3 ? (
            <WatchesForm
              offerType={offerType}
              offeredProducts={sellTradeState.offeredProducts}
              wantedProducts={sellTradeState.wantedProducts}
              manageOfferedProducts={manageOfferedProducts}
              manageWantedProducts={manageWantedProducts}
              addProduct={addProduct}
              applyOffer={applyOffer}
              forModal={forModal}
              loading={loading}
              removeImage={removeImage}
              step={step}
              setStep={setStepForTrade}
            />
          ) : null}
        </>
      ) : null}
    </FormContainer>
  );
};

export default SellTradeForm;
